import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table, Tag, Input, Select, Button, Space, Card, Statistic, Row, Col, message
} from 'antd';
import {
  SearchOutlined, ReloadOutlined, EyeOutlined, ShoppingOutlined,
  DollarOutlined, ClockCircleOutlined, CheckCircleOutlined
} from '@ant-design/icons';
import { apiClient } from 'Utils/api';

const { Search } = Input;
const { Option } = Select;

interface Order {
  id: string;
  orderNumber: string;
  customerEmail: string;
  customerFirstName: string;
  customerLastName: string;
  totalCents: number;
  totalEur: string;
  status: string;
  paymentStatus: string;
  itemCount: number;
  createdAt: string;
}

const OrdersPage: React.FC = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [filters, setFilters] = useState({ status: 'ALL', search: '' });
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    completed: 0,
    revenue: 0,
  });

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line
  }, [pagination.current, filters]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.current.toString(),
        take: pagination.pageSize.toString(),
        ...(filters.status !== 'ALL' && { status: filters.status }),
        ...(filters.search && { search: filters.search }),
      });

      const response = await apiClient.get(`/api/admin/orders?${params}`);
      const { orders: fetchedOrders, meta } = response.data;

      setOrders(fetchedOrders);
      setPagination(prev => ({ ...prev, total: meta.total }));

      // Calculate stats
      calculateStats(fetchedOrders);
    } catch (error: any) {
      console.error('Error fetching orders:', error);
      if (error.response?.status !== 404) {
        message.error(error.response?.data?.message || 'Fehler beim Laden der Bestellungen');
      }
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (ordersData: Order[]) => {
    setStats({
      total: ordersData.length,
      pending: ordersData.filter(o => o.status === 'PENDING_PAYMENT' || o.status === 'PAID').length,
      completed: ordersData.filter(o => o.status === 'COMPLETED').length,
      revenue: ordersData.reduce((sum, o) => sum + (o.totalCents / 100), 0),
    });
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      PENDING_PAYMENT: 'orange',
      PAID: 'blue',
      SHIPPED: 'cyan',
      COMPLETED: 'green',
      CANCELLED: 'red',
    };
    return colors[status] || 'default';
  };

  const getStatusText = (status: string) => {
    const texts: Record<string, string> = {
      PENDING_PAYMENT: 'Zahlung ausstehend',
      PAID: 'Bezahlt',
      SHIPPED: 'Versendet',
      COMPLETED: 'Abgeschlossen',
      CANCELLED: 'Storniert',
    };
    return texts[status] || status;
  };

  const getPaymentStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      PENDING: 'orange',
      PAID: 'green',
      FAILED: 'red',
    };
    return colors[status] || 'default';
  };

  const columns = [
    {
      title: 'Bestellnr.',
      dataIndex: 'orderNumber',
      key: 'orderNumber',
      fixed: 'left' as const,
      width: 150,
    },
    {
      title: 'Kunde',
      key: 'customer',
      render: (_: any, record: Order) => (
        <div>
          <div style={{ fontWeight: 500 }}>
            {record.customerFirstName} {record.customerLastName}
          </div>
          <div style={{ fontSize: 12, color: '#666' }}>{record.customerEmail}</div>
        </div>
      ),
      width: 250,
    },
    {
      title: 'Artikel',
      dataIndex: 'itemCount',
      key: 'itemCount',
      width: 80,
      align: 'center' as const,
    },
    {
      title: 'Gesamt',
      dataIndex: 'totalCents',
      key: 'totalCents',
      width: 120,
      render: (cents: number) => (
        <span style={{ fontWeight: 600 }}>{(cents / 100).toLocaleString('de-DE')} €</span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 150,
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>{getStatusText(status)}</Tag>
      ),
    },
    {
      title: 'Zahlung',
      dataIndex: 'paymentStatus',
      key: 'paymentStatus',
      width: 120,
      render: (status: string) => (
        <Tag color={getPaymentStatusColor(status)}>
          {status === 'PENDING' && 'Ausstehend'}
          {status === 'PAID' && 'Bezahlt'}
          {status === 'FAILED' && 'Fehlgeschlagen'}
        </Tag>
      ),
    },
    {
      title: 'Erstellt',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (date: string) => new Date(date).toLocaleString('de-DE'),
    },
    {
      title: 'Aktionen',
      key: 'actions',
      fixed: 'right' as const,
      width: 240,
      render: (_: any, record: Order) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/orders/${record.id}`)}
          >
            Details
          </Button>
          {record.paymentStatus !== 'PAID' && (
            <Button
              type="primary"
              size="small"
              icon={<CheckCircleOutlined />}
              data-testid={`mark-paid-${record.orderNumber}`}
              onClick={async (e) => {
                e.stopPropagation();
                try {
                  await apiClient.patch(`/api/admin/orders/${record.id}`, {
                    paymentStatus: 'PAID',
                    status: record.status === 'PENDING_PAYMENT' ? 'PAID' : record.status,
                  });
                  message.success('Als bezahlt markiert');
                  fetchOrders();
                } catch (err: any) {
                  message.error(err.response?.data?.message || 'Fehler');
                }
              }}
            >
              Bezahlt
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const handleTableChange = (newPagination: any) => {
    setPagination(prev => ({ ...prev, current: newPagination.current }));
  };

  return (
    <div style={{ padding: 24 }}>
      <h1 style={{ marginBottom: 24 }}>Bestellungen</h1>

      {/* Stats Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Gesamt Bestellungen"
              value={stats.total}
              prefix={<ShoppingOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="In Bearbeitung"
              value={stats.pending}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Abgeschlossen"
              value={stats.completed}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Gesamtumsatz"
              value={stats.revenue}
              precision={2}
              prefix={<DollarOutlined />}
              suffix="€"
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: 16 }}>
        <Space size="middle" style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space>
            <Search
              placeholder="Suche nach Bestellnr., Kunde oder E-Mail"
              allowClear
              enterButton={<SearchOutlined />}
              style={{ width: 400 }}
              onSearch={(value) => {
                setFilters(prev => ({ ...prev, search: value }));
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
            />
            <Select
              value={filters.status}
              style={{ width: 200 }}
              onChange={(value) => {
                setFilters(prev => ({ ...prev, status: value }));
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
            >
              <Option value="ALL">Alle Status</Option>
              <Option value="PENDING_PAYMENT">Zahlung ausstehend</Option>
              <Option value="PAID">Bezahlt</Option>
              <Option value="SHIPPED">Versendet</Option>
              <Option value="COMPLETED">Abgeschlossen</Option>
              <Option value="CANCELLED">Storniert</Option>
            </Select>
          </Space>
          <Button icon={<ReloadOutlined />} onClick={fetchOrders}>
            Aktualisieren
          </Button>
        </Space>
      </Card>

      {/* Orders Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={orders}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: false,
            showTotal: (total) => `Gesamt ${total} Bestellungen`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
        />
      </Card>
    </div>
  );
};

export default OrdersPage;
